import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { ProductImage } from '@app/components/product-image'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import type { ModelSearchMode, ModelListItem, ModelSortOrder } from '@app/features/modeller/api'
import { fetchModelList, modelsKeys } from '@app/features/modeller/api'
import { EMIR_STATUS, formatNumber, formatDate } from '@app/features/modeller/format'
import { StatusBlock, ErrorBlock, SkeletonRows } from '@app/features/modeller/states'

const route = getRouteApi('/_korumali/modeller')

const PAGE_SIZE = 50
/** Photo + the eight data columns — the skeleton draws one cell per column. */
const COLUMN_COUNT = 9

/**
 * Column priority.
 *
 * With the 224px sidebar the table has ~570px at 834 and ~936px at 1200. What
 * survives is what identifies a model and tells you whether it is alive: code,
 * name, colourway count and the last production order. The counts that only
 * matter once you are comparing models (reçete, eşleme, proses) come back with
 * the width.
 */
const COLUMN = {
  sizeSeries: 'hidden min-[1140px]:table-cell',
  process: 'hidden xl:table-cell',
  bom: 'hidden lg:table-cell',
  mapping: 'hidden lg:table-cell',
} as const

const SORT_OPTIONS: ReadonlyArray<{ value: ModelSortOrder; label: string }> = [
  { value: 'lastUsed', label: 'Son kullanım' },
  { value: 'code', label: 'Model kodu' },
  { value: 'color', label: 'Renk sayısı' },
  { value: 'bom', label: 'Reçete satırı' },
]

const SEARCH_MODES: ReadonlyArray<{ value: ModelSearchMode; label: string; hint: string }> = [
  { value: 'model', label: 'Model', hint: 'Model kodu veya adında ara' },
  { value: 'material', label: 'Malzeme', hint: 'Bu malzemeyi kullanan modelleri bul' },
]

export function ModelsScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const query = search.query ?? ''
  const mode = search.mode ?? 'model'
  const sort = search.sort ?? 'lastUsed'
  const page = search.page ?? 1

  /** Writes filters and returns to page 1. */
  // Filter clicks keep the viewport still and carry any text waiting on the
  // search debounce — a click inside the 300 ms window used to discard it.
  function setFilters(next: Partial<typeof search>) {
    const text = flushSearch({ commit: false })
    void navigate({
      search: (prev) => ({
        ...prev,
        query: text === '' ? undefined : text,
        ...next,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  /** Page 1 is the default, so it leaves the URL entirely. */
  function goToPage(next: number) {
    void navigate({ search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }) })
  }

  /*
   * The box keeps its own copy and waits 300 ms so we don't fire a request on
   * every keystroke; `replace` keeps one search from filling the history.
   */
  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text, page: undefined }),
      replace: true,
      // The commit fires mid-typing — a scroll reset would yank the viewport
      // away from the box (see the üretim list for the full story).
      resetScroll: false,
    })
  })

  const params = { query, mode, sort, page, pageSize: PAGE_SIZE }
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: modelsKeys.list(params),
    queryFn: () => fetchModelList(params),
    placeholderData: keepPreviousData,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  /*
   * With `keepPreviousData`, `data` carries the old rows over until the new
   * page arrives; computing the range label from local `page` briefly labeled
   * the old rows with the new page's numbers. Page info is read from the
   * response instead.
   */
  const serverPage = data?.page ?? page
  const serverPageSize = data?.pageSize ?? PAGE_SIZE
  const first = total === 0 ? 0 : (serverPage - 1) * serverPageSize + 1
  const last = Math.min(serverPage * serverPageSize, total)

  /**
   * Clears immediately rather than through the debounce. The box is emptied
   * directly, NOT left to follow the URL: with pending text on a bare URL the
   * clearing navigation is a no-op, so no location change would ever come
   * back to reset it — the box would show "cleared" text forever.
   */
  const clearSearch = () => {
    setSearchInput('')
    setFilters({ query: undefined })
  }

  return (
    <>
      <ScreenHeader
        title="Modeller"
        description="Model kartları, renk varyantları ve proses bazlı reçete — açılır açılmaz son kullanılan modeller listelenir."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-3 py-2.5">
          <div className="relative min-w-52 flex-1">
            <SearchIcon
              aria-hidden
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
            />
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={SEARCH_MODES.find((item) => item.value === mode)?.hint}
              aria-label="Model ara"
              className="pl-7 pr-7"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Aramayı temizle"
                className="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-xs text-ink-muted hover:bg-kagit hover:text-ink"
              >
                <XIcon className="size-3.5" aria-hidden />
              </button>
            ) : null}
          </div>

          {/* Search mode: in Korgun, "which models use this leather" has no screen
              that answers it (docs §3 pain point 7). */}
          <div
            role="group"
            aria-label="Arama modu"
            className="flex shrink-0 items-center rounded-xs border border-hairline bg-surface p-0.5"
          >
            {SEARCH_MODES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilters({ mode: item.value === 'model' ? undefined : item.value })}
                aria-pressed={mode === item.value}
                title={item.hint}
                className={cn(
                  'rounded-xs px-2 py-1 text-micro font-medium transition-colors',
                  mode === item.value
                    ? 'bg-kordovan/10 text-kordovan'
                    : 'text-ink-muted hover:text-ink',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <span className="eyebrow hidden sm:inline">Sırala</span>
            <Select
              value={sort}
              onValueChange={(value) =>
                setFilters({ sort: value === 'lastUsed' ? undefined : (value as ModelSortOrder) })
              }
            >
              <SelectTrigger className="w-38" aria-label="Sıralama">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Exports the whole filtered model list, not just the page on screen. */}
          <Button
            variant="outline"
            className="shrink-0"
            disabled={csv.busy}
            aria-busy={csv.busy}
            onClick={() => csv.start('/models/export', { query: flushSearch(), mode, sort })}
          >
            <DownloadIcon aria-hidden />
            {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
          </Button>

          {csv.error ? (
            <span role="alert" className="text-micro text-hardal">
              {csv.error}
            </span>
          ) : null}
        </div>

        {isError ? (
          <ErrorBlock
            message={error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu.'}
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <TableScroller className={cn(isFetching && !isPending && 'opacity-60')}>
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* No heading over the photos — the pictures are the label. */}
                    <TableHead className="w-9 pr-0" />
                    <TableHead className="w-36">Model kodu</TableHead>
                    <TableHead className="min-w-36">Tanım</TableHead>
                    <TableHead className={cn('w-28', COLUMN.sizeSeries)}>Beden serisi</TableHead>
                    <TableHead className="w-16 text-right">Renk</TableHead>
                    <TableHead className={cn('w-16 text-right', COLUMN.process)}>Proses</TableHead>
                    <TableHead className={cn('w-20 text-right', COLUMN.bom)}>Reçete</TableHead>
                    <TableHead className={cn('w-28', COLUMN.mapping)}>Eşleme</TableHead>
                    <TableHead className="w-36">Son emir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isPending ? (
                    <SkeletonRows rows={12} cols={COLUMN_COUNT} />
                  ) : (
                    items.map((item) => <ModelRow key={item.modelKod} item={item} />)
                  )}
                </TableBody>
              </Table>
            </TableScroller>

            {!isPending && items.length === 0 ? (
              <StatusBlock
                title={
                  mode === 'material'
                    ? 'Bu malzemeyi kullanan model yok'
                    : 'Aramanla eşleşen model yok'
                }
                description={
                  mode === 'material'
                    ? 'Malzeme kodunun bir kısmını yazmayı ya da model aramasına geçmeyi dene.'
                    : 'Model kodunun ya da adının bir kısmını yazmayı dene.'
                }
                action={
                  <Button variant="outline" size="sm" onClick={clearSearch}>
                    Tüm modelleri göster
                  </Button>
                }
              />
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
              <p className="text-micro text-ink-muted">
                {total === 0 ? (
                  'Kayıt yok'
                ) : (
                  <>
                    <span className="font-mono tnum text-ink">
                      {formatNumber(first)}–{formatNumber(last)}
                    </span>{' '}
                    / <span className="font-mono tnum">{formatNumber(total)}</span> model
                  </>
                )}
              </p>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isPending}
                  onClick={() => goToPage(page - 1)}
                >
                  <ChevronLeftIcon aria-hidden />
                  Önceki
                </Button>
                <span className="px-1 font-mono text-micro tnum text-ink-muted">
                  {formatNumber(serverPage)} / {formatNumber(totalPages)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isPending || page >= totalPages}
                  onClick={() => goToPage(Math.min(totalPages, page + 1))}
                >
                  Sonraki
                  <ChevronRightIcon aria-hidden />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  )
}

function ModelRow({ item }: { item: ModelListItem }) {
  const status = item.lastEmirStatus ? EMIR_STATUS[item.lastEmirStatus] : null

  return (
    <TableRow className="relative">
      {/* Not a link itself: the row's overlay anchor is positioned against the
          row and covers this cell too, so the photo shares the one target. */}
      <TableCell className="pr-0">
        <ProductImage imageKey={item.imageKey} alt={`${item.name ?? item.modelKod} fotoğrafı`} />
      </TableCell>
      <TableCell>
        {/* The whole row is clickable because the link overflows the cell. */}
        <Link
          to="/modeller/$modelKod"
          params={{ modelKod: item.modelKod }}
          className="font-mono text-data font-medium text-ink after:absolute after:inset-0 hover:text-kordovan"
        >
          {item.modelKod}
        </Link>
      </TableCell>
      {/* max-w-0 (not max-w-64): `truncate` sets white-space to nowrap, which
          would otherwise pin the column to the longest model name on the page. */}
      <TableCell className="max-w-0 truncate text-ink-muted" title={item.name ?? undefined}>
        {item.name ?? '—'}
      </TableCell>
      <TableCell className={cn('text-ink-muted', COLUMN.sizeSeries)}>
        {item.bedKod ? (
          <span title={item.sizeSeries ?? undefined}>
            <span className="font-mono">{item.bedKod}</span>
          </span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableNumCell className={item.colorCount === 0 ? 'text-ink-muted/45' : undefined}>
        {formatNumber(item.colorCount)}
      </TableNumCell>
      <TableNumCell className={COLUMN.process}>
        {item.emptyProcessCount > 0 ? (
          <span title={`${item.emptyProcessCount} proseste malzeme girilmemiş`} className="text-hardal">
            {formatNumber(item.processCount)}
          </span>
        ) : (
          formatNumber(item.processCount)
        )}
      </TableNumCell>
      <TableNumCell className={cn(item.bomLineCount === 0 && 'text-ink-muted/45', COLUMN.bom)}>
        {formatNumber(item.bomLineCount)}
      </TableNumCell>
      <TableCell className={COLUMN.mapping}>
        {item.mappingCount === 0 ? (
          <span className="text-ink-muted/45">—</span>
        ) : item.differentMappingCount > 0 ? (
          // A mapping isn't an error, it's information: steel tone. Amber is
          // reserved for empty processes only.
          <Badge tone="info" title={`${item.differentMappingCount} satırda farklı beden/renk çekiliyor`}>
            <span className="font-mono tnum">{formatNumber(item.differentMappingCount)}</span> farklı
          </Badge>
        ) : (
          <Badge title="Tüm eşlemeler birebir">
            <span className="font-mono tnum">{formatNumber(item.mappingCount)}</span> birebir
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {item.lastEmirDate ? (
          <span className="flex items-center gap-1.5">
            <span className="font-mono tnum text-ink">{formatDate(item.lastEmirDate)}</span>
            {status ? <Badge tone={status.tone}>{status.label}</Badge> : null}
          </span>
        ) : (
          <span className="text-ink-muted/45">Üretime girmemiş</span>
        )}
      </TableCell>
    </TableRow>
  )
}
